// SkillButtonsAndQuestionGenerator.tsx
import React, { useState, useContext, useCallback, FormEvent, useEffect, useMemo } from 'react';
import { UserContext, SkillData, Skills, UserData, WeeklyProgressUpdate, applyWeeklyProgressToStudyPlan } from '../UserAndProfile/UserContext';
//imports SkillData and Skills type, 
//This is the same format as UserData but used for updating session data if no user is logged in
import { PromptContext } from '../PromptContext';
import { motion, AnimatePresence } from "framer-motion";
import { Box, Button, Typography, TextField, useMediaQuery, Stack, useTheme, Chip } from '@mui/material';
import Grid from '@mui/material/Grid';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import LinearProgress from '@mui/material/LinearProgress';
import ChatComponent from '../ChatComponent';
import { useSearchParams, useParams } from 'react-router-dom';
import theme from '@/muiTheme';
import { CelebrationRenderer, getRandomCelebrationAnimation } from './CelebrationAnimations';
import { getCurrentWeek, getSkillLevel } from '../StudyPlan/studyPlanUtils';
import ReadAloudButton from '../Voice/ReadAloudButton';
import { useVoicePlayback } from '../Voice/VoicePlaybackContext';
import { buildQuestionSpeechText } from '../../utils/textForSpeech';

type SkillButtonsAndQuestionGeneratorProps = {
  selectedSubject: string; // 'Math' or 'Reading and Writing'
  demoMode?: boolean;
  demoSkill?: string;
  demoDifficulty?: 'Easy' | 'Medium' | 'Hard';
};

// Add new types and states
interface SeriesState {
  currentQuestionIndex: number;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  totalQuestionsInSeries: number;
  answers: Array<{
    questionText: string;
    selectedAnswer: string;
    isCorrect: boolean;
  }>;
}

const skillsByType: Record<string, string[]> = {
  "Reading and Writing": ['Function of Sentence', 'Inferences', 'Main Idea', 'Pronouns and Modifiers', 'Punctuation', 'Referencing Data', 'Supporting Claims', "Synthesizing Notes", 'Tenses', 'Transition Words', 'Two Passages', 'Word Choice'],
  "Math": ['Absolute Value', 'Algebra', 'Circles', 'Exponential Equations', 'Exponential Word Problems', 'Geometry', 'Interpreting Graphs', 'Linear Equations', 'Linear Word Problems', 'Inequality Word Problems', 'Percent', 'Polynomial Expressions', 'Probability', 'Quadratic Equations', 'Statistics', 'Systems of Equations', 'Trigonometry', 'Unit Conversions'],
};

const skillFrequency: Record<string, number> = {
  'Function of Sentence': .02,
  'Inferences': .05,
  'Main Idea': .054,
  'Pronouns and Modifiers': .023,
  'Punctuation': .084,
  'Referencing Data': .027,
  'Supporting Claims': .047,
  'Synthesizing Notes': .068,
  'Tenses': .036,
  'Transition Words': .061,
  'Two Passages': .009,
  'Word Choice': .099,
  'Absolute Value': .002,
  'Algebra': .027,
  'Circles': .011,
  'Exponential Equations': .009,
  'Exponential Word Problems': .014,
  'Geometry': .059,
  'Interpreting Graphs': .029,
  'Linear Equations': .045,
  'Linear Word Problems': .059,
  'Inequality Word Problems': .011,
  'Percent': .018,
  'Polynomial Expressions': .016,
  'Probability': .009,
  'Quadratic Equations': .047,
  'Statistics': .014,
  'Systems of Equations': .029,
  'Trigonometry': .014,
  'Unit Conversions': .009
}

// Function to convert display names to skill keys
const skillNameToKey = (skillName: string): string => {
  return skillName.toLowerCase().replace(/ /g, '_');
};

const createInitialSkillData = (): SkillData => ({
  currentTestQuestionsAttempted: 0,
  currentTestQuestionsCorrect: 0,
  currentTestEasyQuestionsAttempted: 0,
  currentTestEasyQuestionsCorrect: 0,
  easyQuestionsIndex: 0,
  currentTestMediumQuestionsAttempted: 0,
  currentTestMediumQuestionsCorrect: 0,
  mediumQuestionsIndex: 0,
  currentTestHardQuestionsAttempted: 0,
  currentTestHardQuestionsCorrect: 0,
  hardQuestionsIndex: 0,
  overallQuestionsAttempted: 0,
  overallQuestionsCorrect: 0
});

const SkillButtonsAndQuestionGenerator: React.FC<SkillButtonsAndQuestionGeneratorProps> = ({ 
  selectedSubject, 
  demoMode = false, 
  demoSkill = 'Linear Equations', 
  demoDifficulty = 'Easy' 
}) => {
  
  const { user, updateUserData } = useContext(UserContext);
  const theme = useTheme();
  const {questionData, questionText, choices, correctAnswer, explanationText, setQuestionText, setChoices, setCorrectAnswer, setExplanationText, 
    pictureURL, setPictureURL, selectedSkill, setSelectedSkill, selectedDifficulty, setSelectedDifficulty, clearChat, renderLatexOrText, 
    interactionStage, setInteractionStage, isSeriesActive, setIsSeriesActive} = useContext(PromptContext)!; //'!' is used to assert that PromptContext is not null because PromptProvider wraps all of SecondPage.tsx
    //interactionStage is used by ChatComponent to dynamically change quickPrompts - don't delete!
  
    //New on 4/12/2025 to handle URL parameters for Practice Similar Questions redirect from Test Results Pages:
  const [searchParams, setSearchParams] = useSearchParams();
  //ensures that the URL parameters are only used once, and not on regular page load:
  const [initializedFromParams, setInitializedFromParams] = useState(false); 
  const {subject: subjectFromParams} = useParams<{subject?: string}>();
  
  const [selectedAnswer, setSelectedAnswer] = useState<string>('');
  const [freeResponseInput, setFreeResponseInput] = useState<string>(''); // New: State for free-response input
  const [feedback, setFeedback] = useState<string>('');
  const [isAnswerSubmitted, setIsAnswerSubmitted] = useState<boolean>(false);
  const [incorrectAnswers, setIncorrectAnswers] = useState<string[]>([]); //incorrect answers that have been subitted, so the user can try again
  const [hasSubmittedFirstAttempt, setHasSubmittedFirstAttempt] = useState<boolean>(false);
  const [seriesState, setSeriesState] = useState<SeriesState>({
    currentQuestionIndex: 0,
    difficulty: 'Easy',
    totalQuestionsInSeries: 5,
    answers: [],
  });
  const [sessionIndices, setSessionIndices] = useState<Skills>({}); //mirrors userData's skills object
  const [celebrationAnimation, setCelebrationAnimation] = useState<number | null>(null); // Track which celebration to show
  const isMobile = useMediaQuery('(max-width: 600px)');
  const { stop: stopVoicePlayback } = useVoicePlayback();

  const questionSpeechText = useMemo(
    () =>
      questionText
        ? buildQuestionSpeechText({
            question: questionText,
            choices: Array.isArray(choices) ? choices : [],
            pictureURL: pictureURL ?? null,
          })
        : '',
    [questionText, choices, pictureURL]
  );

  useEffect(() => {
    stopVoicePlayback();
  }, [seriesState.currentQuestionIndex, selectedSkill, stopVoicePlayback]);

  const updateProgress = (skillKey: string, updates: Partial<SkillData>, weeklyProgressUpdate?: WeeklyProgressUpdate) => {
    const currentData = user?.skills[skillKey] || sessionIndices[skillKey] || createInitialSkillData();
    const updatedData = { ...currentData, ...updates };
  
    if (user) {//only updates user if user is logged in
      const userUpdates: Partial<UserData> = {
        skills: { ...user.skills, [skillKey]: updatedData }
      };

      // Track attempted questions
      if (updates.overallQuestionsAttempted && updates.overallQuestionsAttempted > currentData.overallQuestionsAttempted) {
        const attemptedIncrement = updates.overallQuestionsAttempted - currentData.overallQuestionsAttempted;
        userUpdates.grandTotalQuestionsAttempted = (user.grandTotalQuestionsAttempted || 0) + attemptedIncrement;
        
        // Update study plan skill practice goals when questions are attempted
        if (user.studyPlan) {
          const updatedStudyPlan = { ...user.studyPlan };
          
          // Update overall skill practice goal
          if (updatedStudyPlan.overallSkillPracticeQuestionGoal) {
            updatedStudyPlan.overallSkillPracticeQuestionGoal.current = 
              (updatedStudyPlan.overallSkillPracticeQuestionGoal.current || 0) + attemptedIncrement;
          }
          
          // Update weekly skill practice goal
          const today = new Date();
          const currentWeek = getCurrentWeek(updatedStudyPlan, today);
          if (currentWeek && currentWeek.weeklySkillsPracticeQuestionGoal) {
            const weekIndex = currentWeek.weekNumber - 1;
            if (updatedStudyPlan.weeklyPlans[weekIndex]?.weeklySkillsPracticeQuestionGoal) {
              updatedStudyPlan.weeklyPlans[weekIndex].weeklySkillsPracticeQuestionGoal.current =
                (updatedStudyPlan.weeklyPlans[weekIndex].weeklySkillsPracticeQuestionGoal.current || 0) + attemptedIncrement;
            }
          }
          
          userUpdates.studyPlan = updatedStudyPlan;
          console.log(`Updated study plan skill practice goals: +${attemptedIncrement} questions`);
        }
      }

      // Track correct questions
      if (updates.overallQuestionsCorrect && updates.overallQuestionsCorrect > currentData.overallQuestionsCorrect) {
        const correctIncrement = updates.overallQuestionsCorrect - currentData.overallQuestionsCorrect;
        userUpdates.grandTotalQuestionsCorrect = (user.grandTotalQuestionsCorrect || 0) + correctIncrement;
      }
      
      // NEW: Apply weekly progress tracking to study plan if provided
      if (weeklyProgressUpdate && user.studyPlan && userUpdates.studyPlan) {
        userUpdates.studyPlan = applyWeeklyProgressToStudyPlan(userUpdates.studyPlan, weeklyProgressUpdate);
        console.log(`✅ Weekly progress updated: +${weeklyProgressUpdate.questionsAttempted} questions, ${Object.keys(weeklyProgressUpdate.skillUpdates).length} skills`);
      }
      
      updateUserData(userUpdates); // Single Firestore write with all updates merged
    }
  
    setSessionIndices(prev => ({//always updates sessionIndices
      ...prev,
      [skillKey]: updatedData
    }));
  };
  
  const checkAndUpdatePracticeDate = () => {
    if (!user) return;

    const today = new Date().toISOString().split('T')[0]; // Get current date in YYYY-MM-DD format
    if (user.lastPracticedDate !== today) {
      // If the last practiced date is not today, increment totalDaysPracticed
      updateUserData({
        totalDaysPracticed: user.totalDaysPracticed + 1,
        lastPracticedDate: today,
      });
    }
  };


  const generateQuestion = useCallback(async (skill: string, difficulty: 'Easy' | 'Medium' | 'Hard', isPrevious: boolean = false) => {
    if (skill === "") {
      console.error('No skill selected. Please try again.');
      return;
    }
    
    //This will make skill accessible throughout components.
    setSelectedSkill(skill);
    setSelectedDifficulty(difficulty);
    setFeedback(''); //clears the feedback from the previous question
    clearChat(); //clears Chat history (in PromptContext) when new question is generator to not clog up ChatComponent
    setSelectedAnswer(''); //clears previous answer choice (A, etc.) so it's not highlighted if the same skill question is generated
    setCorrectAnswer(''); //clear previous correct answer (D, etc.) so it's not highlighted if the same skill question is generated
    setIsAnswerSubmitted(false);
    setIncorrectAnswers([]) //reset incorrect answers from the previous question
    setHasSubmittedFirstAttempt(false);

    try {
      if (!questionData) throw new Error('Questions are not loaded yet.');

      // Retrieve current question index based on difficulty
      const skillKey = skillNameToKey(skill);
      const currentData = user?.skills[skillKey] || sessionIndices[skillKey] || createInitialSkillData();
      const difficultyKey = `${difficulty.toLowerCase()}QuestionsIndex` as keyof SkillData; //"keyof SkillData" will update if SkillData changes vs. explicit text above
      //Ex of difficultyKey: 'easyQuestionsIndex' | 'mediumQuestionsIndex' | 'hardQuestionsIndex'

      //Filter questions based on skill and difficulty
      // 🔍 DEBUG: Log the data access
      console.log(`🔍 Debug generateQuestion:`, {
        skill,
        skillKey,
        difficulty,
        difficultyKey,
        currentData,
        indexValue: currentData[difficultyKey]
      });

      // Filter questions based on skill and difficulty
      const questions = questionData.filter(q => q.skill === skill && q.difficulty === difficulty);
      console.log(`${questions.length} ${difficulty} questions for ${skill}.`);

      if (questions.length === 0) {
        throw new Error(`No questions found for ${skill} at ${difficulty} difficulty.`);
      }

      // 🛠️ FIX: Ensure we have a valid index
      const rawIndex = currentData[difficultyKey];
      const safeCurrentIndex = typeof rawIndex === 'number' && !isNaN(rawIndex) ? rawIndex : 0;
      
      const currentQuestionIndex = isPrevious? 
        (safeCurrentIndex - 2 + questions.length) % questions.length // Going backwards 2 and forwards 1; adding questions.length then dividing and taking remainder means it always works
        : safeCurrentIndex;

      const newQuestionIndex = (currentQuestionIndex + 1) % questions.length;
      
      console.log(`�� Index calculation:`, {
        rawIndex,
        safeCurrentIndex,
        currentQuestionIndex,
        newQuestionIndex,
        questionsLength: questions.length
      });

      if (isNaN(newQuestionIndex)) {
        console.error('🚨 newQuestionIndex is NaN!', { currentQuestionIndex, questions: questions.length });
        return;
      }

      const selectedQuestion = questions[currentQuestionIndex]

      // Set the state variables with the selected question data
      setQuestionText(selectedQuestion.question);
      setChoices(selectedQuestion.choices || '');
      setCorrectAnswer(selectedQuestion.answer);
      setExplanationText(selectedQuestion.explanation || '');
      setPictureURL(selectedQuestion.pictureURL || null);
      // Set interaction stage after successfully generating a question for use when rendering quickPrompts - pre-question, during, or after submitting answer?
      setInteractionStage('questionGenerated'); //used for dynamically changing quickPrompt questions in ChatComponent

      // Only update the next index for this difficulty; do NOT increment attempted counters here
      updateProgress(skillKey, {
        [difficultyKey]: newQuestionIndex
      });
      
    } catch (error) {
      console.error('Error generating question:', error);
    } 
  }, [selectedSubject, selectedSkill, setSelectedSkill, clearChat, updateProgress, user, sessionIndices, questionData]); //Should I remove the comma after user?



  // New function to start question series
  const startQuestionSeries = useCallback(async (difficulty: 'Easy' | 'Medium' | 'Hard', skillOverrideFromPracticeSimilarQuestionsInTestResultsPageOrDashboardSkillsByDifficultyChart?: string) => {
    const skillToUse = skillOverrideFromPracticeSimilarQuestionsInTestResultsPageOrDashboardSkillsByDifficultyChart || selectedSkill; // Use override if provided, else use context state
    if (!skillToUse) {
      console.error("Cannot start series: No skill selected or provided.");
      return; // Prevent starting if no skill is available
    }

    // CRITICAL: Set the skill in context FIRST before generating questions
    if (skillOverrideFromPracticeSimilarQuestionsInTestResultsPageOrDashboardSkillsByDifficultyChart) {
      console.log(`🔄 Overriding skill context: ${selectedSkill} → ${skillOverrideFromPracticeSimilarQuestionsInTestResultsPageOrDashboardSkillsByDifficultyChart}`);
      setSelectedSkill(skillOverrideFromPracticeSimilarQuestionsInTestResultsPageOrDashboardSkillsByDifficultyChart);
      setSelectedDifficulty(difficulty);
    }
    
    // Clear any previous state
    setQuestionText('');
    setChoices([]);
    setCorrectAnswer('');
    setExplanationText('');
    setPictureURL(null);
    clearChat();
    
    //Update totalDaysPracticed and lastPracticedDate if today's date is different from the last time practiced
    checkAndUpdatePracticeDate(); //used for PersonalizedBar.tsx to display Days in a Streak and Total Days Practiced
    setIsSeriesActive(true);
    setSeriesState({
      currentQuestionIndex: 0,
      difficulty,
      totalQuestionsInSeries: 5,
      answers: [],
    });
    
    await generateQuestion(skillToUse, difficulty);
    //***Double check on 9/30/2025 thaht I didn't add too many dependencies to this: setQuestionText, setChoices, setCorrectAnswer, setExplanationText, setPictureURL
  }, [selectedSkill, setSelectedSkill, setSelectedDifficulty, generateQuestion, setQuestionText, setChoices, setCorrectAnswer, setExplanationText, setPictureURL]);

  
  
  //NEW on 4/12/2025: useEffect to handle URL parameters from Practice Similar Questions redirect from Test Results Pages ---
  useEffect(() => {
    const skillParam = searchParams.get('skill');
    const difficultyParam = searchParams.get('difficulty') as 'Easy' | 'Medium' | 'Hard' | null;
    
    console.log("🔄 useEffect triggered:", { 
      skillParam, 
      difficultyParam, 
      isSeriesActive, 
      initializedFromParams, 
      hasQuestionData: !!questionData 
    });

    // Only run if params exist, series isn't already active, we haven't initialized from params yet, AND questionData is loaded
    if (skillParam && difficultyParam && !isSeriesActive && !initializedFromParams && questionData) {
      const decodedSkillParam = decodeURIComponent(skillParam);
      
      console.log(`�� URL Navigation Detected:`);
      console.log(`   Current selectedSkill in context: "${selectedSkill}"`);
      console.log(`   New skill from URL: "${decodedSkillParam}"`);
      console.log(`   Difficulty: ${difficultyParam}`);
      
      setInitializedFromParams(true); // Set this FIRST to prevent re-runs
      
      // Use setTimeout to ensure state updates are processed
      setTimeout(() => {
        startQuestionSeries(difficultyParam, decodedSkillParam);
      }, 0);
      
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, isSeriesActive, initializedFromParams, questionData]); // Removed selectedSkill to prevent unnecessary re-runs

  // NEW: useEffect to handle demo mode auto-start
  useEffect(() => {
    if (demoMode && !isSeriesActive && !initializedFromParams && demoSkill && demoDifficulty && questionData) {
      console.log(`Demo mode: Auto-starting with ${demoSkill} at ${demoDifficulty} difficulty`);
      // Auto-start the demo series
      startQuestionSeries(demoDifficulty, demoSkill);
    }
  }, [demoMode, isSeriesActive, initializedFromParams, demoSkill, demoDifficulty, startQuestionSeries, questionData]);

  // Toggle skill selection based on which skill box the user clicks
  //Should probably implmment "updateUIState" to box up the setQuestionText, setChoice, etc. to blank
  const handleSkillClick = (skill: string) => {
    setSelectedSkill(skill === selectedSkill ? "" : skill); 
    // Clear previous question and feedback when changing skill. Added at 11:00 am on 9/19
    setQuestionText('');
    setChoices([]);
    setCorrectAnswer('');
    setExplanationText('');
    setSelectedAnswer('');
    setPictureURL(null);
    setFeedback('');
    setInteractionStage('notStarted');
  };  

  // Simplified helper function to get data from either user or session
  //Do I need this?
  const getSkillData = (skillKey: string) => {
    // Return user data if it exists, otherwise return session data
    return user?.skills[skillKey] || sessionIndices[skillKey] || {
      currentTestEasyQuestionsAttempted: 0,
      currentTestEasyQuestionsCorrect: 0,
      currentTestMediumQuestionsAttempted: 0,
      currentTestMediumQuestionsCorrect: 0,
      currentTestHardQuestionsAttempted: 0,
      currentTestHardQuestionsCorrect: 0
    };
  };

  // Function to calculate overall skill percentage correct from user data
  const calculateSpecificDifficultyPercentage = (skill: string, difficulty: 'Easy' | 'Medium' | 'Hard') => {
    const skillData = getSkillData(skillNameToKey(skill));
    console.log(`skillData is ${skillData}. That means ${skillData[`currentTest${difficulty}QuestionsAttempted`]} attempted and ${skillData[`currentTest${difficulty}QuestionsCorrect`]} correct.`);
    const attempted = skillData[`currentTest${difficulty}QuestionsAttempted`] || 0;
    const correct = skillData[`currentTest${difficulty}QuestionsCorrect`] || 0;
    console.log(`New ${skill} ${difficulty} percentage is ${Math.round((correct / attempted) * 100)}`);
    
    return attempted === 0 ? 0 : Math.round((correct / attempted) * 100);
  };

  // Simplified attempts function
  const getAttemptsForDifficulty = (skill: string, difficulty: 'Easy' | 'Medium' | 'Hard') => {
    const skillData = getSkillData(skillNameToKey(skill));
    return skillData[`currentTest${difficulty}QuestionsAttempted`] || 0;
  };

  // Simplified attempts function
  const getCorrectForDifficulty = (skill: string, difficulty: 'Easy' | 'Medium' | 'Hard') => {
    const skillData = getSkillData(skillNameToKey(skill));
    return skillData[`currentTest${difficulty}QuestionsCorrect`] || 0;
  };

  //This is where setFeedback can be helpful when making AN INCORRECT GUESS
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    const answer = choices.length === 0 ? freeResponseInput.trim() : selectedAnswer;
    console.log(`freeResponseInput is: ${freeResponseInput}`);
    
    if (answer === '') {
      if (freeResponseInput.trim() === '') { //if it's a free response question
        setFeedback('Please enter an answer in the input box.');
        console.log(`There was no input.`);
        return;
      } else { //it's a multiple choice question
        setFeedback('Please select an answer.');
        return;
      }
    }
    const isCorrect = answer === correctAnswer;
    console.log(`answer is ${answer} and correctAnswer is ${correctAnswer} and isCorrect is ${isCorrect}`);
    /****Opportunity to provide more helpful feedback on Incorrect answers 
    RETURN HERE TO MAKE THIS BETTER IN THE FUTURE! */
    setFeedback(isCorrect ? 'Correct!' : 'Nice try but not quite - can you figure out what happened?');
    setIsAnswerSubmitted(true);
    setInteractionStage('answered');

    if (!isCorrect && choices.length > 0){ //if incorrect answer, add it to an array to keep it highlighted red
      setIncorrectAnswers((prev) => [...prev, selectedAnswer])
    } //****Might have an async problem where I need to do "setTimeout" if this doesn't work: */

    console.log(`right before checking if hasSubmittedFirstAttempt is true or false`);
    if (!hasSubmittedFirstAttempt && selectedSkill) {
      setHasSubmittedFirstAttempt(true); // Mark as the first attempt to prevent further updates
      const skillKey = skillNameToKey(selectedSkill);
      const currentData = user?.skills[skillKey] || sessionIndices[skillKey] || createInitialSkillData();

      // On first submission, increment attempted counters. Increment correct counters only if correct.
      const attemptedKey = `currentTest${selectedDifficulty}QuestionsAttempted` as keyof SkillData;
      const correctKey = `currentTest${selectedDifficulty}QuestionsCorrect` as keyof SkillData;

      // Build weekly progress update for comprehensive activity tracking
      const weeklyProgressUpdate: WeeklyProgressUpdate | undefined = (user && user.studyPlan) ? {
        questionsAttempted: 1,
        questionsCorrect: isCorrect ? 1 : 0,
        activityType: 'skill-practice',
        skillUpdates: {
          [selectedSkill]: {
            attempted: 1,
            correct: isCorrect ? 1 : 0,
            difficulty: selectedDifficulty.toLowerCase() as 'easy' | 'medium' | 'hard',
          },
        },
        studyMinutes: 2, // Estimate 2 minutes per question
      } : undefined;

      // Single updateUserData call with all updates merged
      updateProgress(skillKey, {
        [attemptedKey]: (currentData[attemptedKey] as number) + 1,
        [correctKey]: isCorrect ? (currentData[correctKey] as number) + 1 : (currentData[correctKey] as number),
        overallQuestionsAttempted: (currentData.overallQuestionsAttempted || 0) + 1,
        overallQuestionsCorrect: isCorrect ? (currentData.overallQuestionsCorrect || 0) + 1 : (currentData.overallQuestionsCorrect || 0)
      }, weeklyProgressUpdate);
    }
    //New as of 1/23/2025 after hearing from student who couldnt' submit Free Response Questions:
    else if (choices.length === 0 && !hasSubmittedFirstAttempt) {
      setHasSubmittedFirstAttempt(true); //Mark as first attempt to log to user
    }
  };

  // Watch for correct answers and trigger celebration
  useEffect(() => {
    if (isAnswerSubmitted && feedback === 'Correct!' && !celebrationAnimation) {
      setCelebrationAnimation(getRandomCelebrationAnimation());
    }
  }, [isAnswerSubmitted, feedback]); // Removed celebrationAnimation from dependencies to prevent loop

  // Clear celebration animation after timeout
  useEffect(() => {
    if (celebrationAnimation) {
      const timer = setTimeout(() => {
        setCelebrationAnimation(null);
      }, 3000); 
      return () => clearTimeout(timer);
    }
  }, [celebrationAnimation]);

  const handleNextQuestion = async () => {
    // Reset all question-related states
    setSelectedAnswer('');
    setFeedback('');
    setIsAnswerSubmitted(false);
    setIncorrectAnswers([]);
    setHasSubmittedFirstAttempt(false);
    setFreeResponseInput('');
    setCelebrationAnimation(null); // Clear any celebration animation
  
    // Increment question index
    setSeriesState(prev => ({
      ...prev,
      currentQuestionIndex: prev.currentQuestionIndex + 1
    }));
  
    // Generate new question
    await generateQuestion(selectedSkill, seriesState.difficulty);
  };

  const handlePreviousQuestion = async () => {
    // Reset all question-related states
    setSelectedAnswer('');
    setFeedback('');
    setIsAnswerSubmitted(false);
    setIncorrectAnswers([]);
    setHasSubmittedFirstAttempt(false);
    setFreeResponseInput('');
    setCelebrationAnimation(null); // Clear any celebration animation
  
    // Increment question index
    setSeriesState(prev => ({
      ...prev,
      currentQuestionIndex: prev.currentQuestionIndex - 1
    }));
    updateProgress(skillNameToKey(selectedSkill), {
      [`${selectedDifficulty.toLowerCase()}QuestionsIndex`]: seriesState.currentQuestionIndex - 1
    });

    // Generate new question
    await generateQuestion(selectedSkill, seriesState.difficulty, true);
  };
  
  // Add handleSkip function
  const handleSkip = async () => {
    // No counter changes on skip. Simply proceed to next question.
    await handleNextQuestion();
  };

  const handleExtendSeries = async () => {
    setCelebrationAnimation(null); // Clear any celebration animation
    setSeriesState(prev => ({
      ...prev,
      totalQuestionsInSeries: prev.totalQuestionsInSeries + 5,
      currentQuestionIndex: prev.currentQuestionIndex + 1
    }));
    await generateQuestion(selectedSkill, seriesState.difficulty);
  };


  return (
    <Box sx={{width: '100%', p: {xs: 1, sm: 2}}}>
      <AnimatePresence>
        {!isSeriesActive && (
          <motion.div
            initial={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            transition={{ duration: 0.3 }}
          >
            <Grid container spacing={2}>
              {skillsByType[selectedSubject].map((skill) => {
                const isSelected = selectedSkill === skill;
                const skillKey = skillNameToKey(skill);
                const skillData = user?.skills?.[skillKey];
                const attempted = skillData?.overallQuestionsAttempted || 0;
                const correct = skillData?.overallQuestionsCorrect || 0;
                const level = attempted >= 4 ? getSkillLevel(Math.round((correct / attempted) * 100)) : null;

                return (
                  <Grid component="div" size={{xs:6, sm:4, md:3, lg:2}} key={skill}>
                    <Button
                      variant={isSelected ? 'contained' : 'outlined'}
                      onClick={() => handleSkillClick(skill)}
                      sx={{
                        width: '100%',
                        minHeight: 50,
                        flexDirection: 'column',
                        gap: 0.25,
                        py: 0.75,
                        bgcolor: isSelected ? 'primary.main' : 'background.paper',
                        '&:hover': {
                          bgcolor: isSelected ? 'primary.dark' : 'grey.100',
                        },
                      }}
                    >
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, lineHeight: 1.2 }}>
                        {skill}
                      </Typography>
                      {level && (
                        <Chip label={level.label} size="small" color={level.color}
                          sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }} />
                      )}
                    </Button>
                  </Grid>
                );
              })}
            </Grid>

            {/* Difficulty buttons for series */}
            {selectedSkill && (
              
              <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box sx={{display: 'flex', justifyContent: {xs: 'space-evenly', sm: 'center'}, gap: {xs: 1.5, sm: 2}, width: '100%'}}>
                  {['Easy', 'Medium', 'Hard'].map((difficulty) => {
                    //const percentCorrect = calculateSpecificDifficultyPercentage(selectedSkill, difficulty as 'Easy' | 'Medium' | 'Hard');
                    const attempted = getAttemptsForDifficulty(selectedSkill, difficulty as 'Easy' | 'Medium' | 'Hard');
                    const numCorrect = getCorrectForDifficulty(selectedSkill, difficulty as 'Easy' | 'Medium' | 'Hard');
                    
                    return (
                      <Box key={difficulty} sx={{display: 'flex', flexDirection: 'column', 
                        alignItems: 'center', minWidth: {xs: 80, sm: 160}}}>
                        <Typography variant="body1" sx={{ mb: 1, fontSize: {xs: '.85rem', sm: 16}}}>{numCorrect} / {attempted} correct</Typography>
                        {/*Both of these below were swapped on 8/11/2025 based on Chris O'Sullivan's suggestion.
                        
                        <Typography variant="body1" sx={{ mb: 1, fontSize: {xs: '.85rem', sm: 16}}}>{percentCorrect}% correct</Typography>
                        <Typography variant="body1" sx={{ mb: 2, fontSize: {xs: '.85rem', sm: 16}}}>{attempted} attempted</Typography>*/}
                        <Button key={difficulty} variant="contained"
                          onClick={() => startQuestionSeries(difficulty as 'Easy' | 'Medium' | 'Hard')}
                          sx={{ fontSize: {xs: '.875rem', sm: 16}, padding: {xs: 0, sm: 2}, maxWidth: {xs: 130, sm: 160, md: 204}, height: {xs: 72, sm: 60}}}
                        >
                          Practice {difficulty} Questions
                        </Button>
                    </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
          </motion.div>
        )}


        {/* Series mode UI */}
        {isSeriesActive && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Progress Bar */}
            <Box sx={{ width: '100%', mb: 2 }}>
              <LinearProgress variant="determinate" 
                value={((seriesState.currentQuestionIndex + 1) / seriesState.totalQuestionsInSeries) * 100}
                  sx={{height: 10, borderRadius: 5, backgroundColor: 'grey.200',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 5, backgroundColor: 'custom.green', // Consider if this green should match the new softer green
                    },
                  }}
              />
              <Box sx={{mt: 1, display: 'flex', justifyContent: 'space-between', px: 1, 
                fontSize: '0.875rem', color: 'text.secondary',
                }}
              >
                <span>0</span>
                <span>{Math.floor(seriesState.totalQuestionsInSeries * .2)}</span>
                <span>{Math.floor(seriesState.totalQuestionsInSeries * .4)}</span>
                <span>{Math.floor(seriesState.totalQuestionsInSeries * .6)}</span>
                <span>{Math.floor(seriesState.totalQuestionsInSeries * .8)}</span>
                <span>{seriesState.totalQuestionsInSeries}</span>
              </Box>
            </Box>

            {/* Header: Exit, Skill, Question Count */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: {xs: 0, sm: 2}, mb: {xs: 2, sm: 3} }}>
              <Box sx={{ flex: '0 0 auto' }}>
                <Button variant="contained" startIcon={<ArrowBackIcon />}
                  onClick={() => {
                    console.log(`Exiting series for skill: ${selectedSkill}`);
                    setIsSeriesActive(false);
                    setPictureURL(null);
                    setCelebrationAnimation(null); // Clear any celebration animation
                    
                    // Complete state reset
                    setQuestionText('');
                    setChoices([]);
                    setCorrectAnswer('');
                    setExplanationText('');
                    setSelectedSkill(''); // Clear the skill selection
                    setInteractionStage('notStarted');
                    clearChat();
                    
                    // Reset local component state
                    setSelectedAnswer('');
                    setFeedback('');
                    setIsAnswerSubmitted(false);
                    setIncorrectAnswers([]);
                    setHasSubmittedFirstAttempt(false);
                    setFreeResponseInput('');
                }}
                sx={{bgcolor: theme.palette.primary.main, '&:hover': {bgcolor: theme.palette.primary.dark}, borderRadius: 10,
                width: { xs: '95px', sm: 'auto' }}}
                >
                  <Typography variant="body1" sx={{
                    color: 'white', 
                    wordWrap: 'break-word', 
                    overflowWrap: 'break-word',
                    whiteSpace: { xs: 'pre-wrap', sm: 'normal' }, // Allow wrapping on xs screens
                    lineHeight: { xs: 1.2, sm: 1.5 }, // Tighter line height on xs
                  }}>
                    Exit Series
                  </Typography>
                </Button>
              </Box>
              <Box sx={{ flex: '1 1 auto' }}>              
                <Typography variant="h5" sx={{textAlign: 'center'}}>{selectedSkill}</Typography>
              </Box>
              <Box sx={{flex: '0 1 auto', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, maxWidth: {xs: '160px', sm: '240px'}}}>
                {questionSpeechText && (
                  <ReadAloudButton
                    id={`skill-q-${selectedSkill}-${seriesState.currentQuestionIndex}`}
                    text={questionSpeechText}
                    alreadyNormalized
                    ariaLabel="Read question and choices aloud"
                  />
                )}
                <Typography variant="h6" sx={{textAlign: 'center', wordWrap: 'break-word', overflowWrap: 'break-word'}}>
                  Question {seriesState.currentQuestionIndex + 1} of {seriesState.totalQuestionsInSeries}
                </Typography>
              </Box>
            </Box>

            {/* Question display and controls */}
            <Box sx={{ mb: 0 }}>
              {/* Wrap Question and Choices/Input in a Flex container */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 3 } }}>
                {/* Left Column: Question Text */}
                <Box sx={{ flex: { md: 1 }, minWidth: { md: '50%' } }}>
                  {pictureURL && (
                    <Box sx={{ mb: 2, textAlign: 'center' }}>
                      <img src={pictureURL}
                        alt={`Diagram for question ${seriesState.currentQuestionIndex + 1}`}
                        style={{ maxWidth: '100%', height: 'auto', display: 'block', margin: '0 auto', borderRadius: '4px', border: `1px solid ${theme.palette.divider}` }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                          console.warn(`Failed to load image: ${pictureURL}`);
                        }}
                      />
                    </Box>
                  )}
                  {questionText && renderLatexOrText(questionText)}
                </Box>

                {/* Right Column: Choices or Free Response Input */}
                <Box sx={{ flex: { md: 1 }, minWidth: { md: '45%' } }}>
                  {/* Conditionally render the answer options or the free-response form */}
                  {questionText && choices.length === 0 ? (
                    <form onSubmit={handleSubmit}>
                      <TextField fullWidth variant="outlined" placeholder="Enter your answer..."
                          value={freeResponseInput}
                          onChange={(e) => setFreeResponseInput(e.target.value)}
                          // Disable input only after correct submission (feedback check)
                          disabled={isAnswerSubmitted && feedback === 'Correct!'}
                          sx={{ mb: 2,'& .MuiOutlinedInput-root': {
                              '&.Mui-focused fieldset': {
                                // Keep existing focus border logic for free response
                                borderColor: feedback === 'Correct!' ? '#4CAF50' : feedback ? '#F44336' : undefined,
                              },
                            },
                          }}
                        />
                    </form>
                    ) : (
                    // Use Stack for vertical arrangement of buttons in the right column
                    <Stack spacing={1.5}>
                      {choices.map((choice, index) => {
                        const choiceLetter = String.fromCharCode(65 + index);
                        const isSelected = selectedAnswer === choiceLetter;
                        const isCorrect = choiceLetter === correctAnswer;
                        // Keep track of which specific incorrect answers were submitted
                        const isMarkedIncorrect = incorrectAnswers.includes(choiceLetter);
                        // Determine if the user has submitted the final correct answer
                        const isFinalCorrect = isAnswerSubmitted && feedback === 'Correct!';
                        // Determine when to show this specific choice as green
                        const showGreen = isFinalCorrect && isCorrect;
                        // Determine when to show this specific choice as red
                        const showRed = isMarkedIncorrect;
                        // Determine when to show the light blue selection highlight
                        const showVeryLightOrange = isSelected && !isAnswerSubmitted; // Only show blue before any submission attempt

                        // Define softer colors
                        const softerGreenBg = 'rgba(76, 175, 80, 0.15)';
                        const softerGreenBorder = '#4CAF50';
                        const softerRedBg = 'rgba(244, 67, 54, 0.15)';
                        const softerRedBorder = '#F44336';
                        // Define selected color (using light blue as before)
                        const veryLightOrange = 'custom.veryLightOrange'; // Or theme.palette.custom.veryLightOrange
                        const selectedBorder = 'primary.main';

                        return (
                          // Using Button for semantic correctness, styled like the target Box/Paper
                          <Button
                            key={index}
                            fullWidth
                            variant={'outlined'} // Base variant is outlined
                            onClick={() => {
                              if (!isFinalCorrect) { // Prevent changing selection after correct answer submitted
                                setSelectedAnswer(choiceLetter);
                                // Reset feedback/submission status if user changes answer *after* an incorrect submission,
                                // but KEEP the incorrectAnswers array to maintain styling on previously wrong choices.
                                if (isAnswerSubmitted && feedback !== 'Correct!') {
                                    setIsAnswerSubmitted(false);
                                    // setIncorrectAnswers([]); // DO NOT reset this here
                                    setFeedback('');
                                }
                              }
                            }}
                            // Disable all buttons once the correct answer is submitted and confirmed
                            disabled={isFinalCorrect}
                            sx={{
                              p: 1.5, // Padding like Paper
                              mb: 0, // Stack handles spacing
                              borderRadius: 2, // Rounded corners
                              border: 2, // Border width like Diagnostic/Review
                              minHeight: '48px', // Ensure consistent height
                              justifyContent: 'flex-start', // Align text left
                              textTransform: 'none', // Prevent uppercase text
                              transition: 'all 0.2s ease', // Smooth transitions

                              // --- Dynamic Styling ---
                              borderColor: showGreen ? softerGreenBorder
                                          : showRed ? softerRedBorder
                                          : showVeryLightOrange ? selectedBorder
                                          : 'divider', // Default border
                              bgcolor: showGreen ? softerGreenBg
                                      : showRed ? softerRedBg
                                      : showVeryLightOrange ? veryLightOrange
                                      : 'background.paper', // Default background
                              // Use default text color for better readability on light backgrounds
                              color: 'text.primary',

                              cursor: isFinalCorrect ? 'default' : 'pointer',

                              // --- Hover Styling ---
                              '&:hover': {
                                // Keep border color based on state, slightly darken default border
                                borderColor: showGreen ? softerGreenBorder
                                            : showRed ? softerRedBorder
                                            : showVeryLightOrange ? selectedBorder
                                            : 'grey.400',
                                // Keep background color based on state, use standard light gray for default hover
                                bgcolor: showGreen ? softerGreenBg
                                        : showRed ? softerRedBg
                                        : showVeryLightOrange ? veryLightOrange // Keep selected color on hover
                                        : 'rgba(0, 0, 0, 0.04)',
                                // Keep text color consistent
                                color: 'text.primary',
                                // Apply transform/shadow only when interaction is allowed
                                transform: isFinalCorrect ? 'none' : 'scale(1.01)',
                                boxShadow: isFinalCorrect ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.1)',
                              },

                              // --- Disabled Styling ---
                              '&.Mui-disabled': {
                                  borderColor: showGreen ? softerGreenBorder : showRed ? softerRedBorder : 'action.disabledBackground',
                                  bgcolor: showGreen ? softerGreenBg : showRed ? softerRedBg : 'action.disabledBackground',
                                  // Use disabled text color unless it's green/red state
                                  color: showGreen || showRed ? 'text.primary' : 'action.disabled',
                                  opacity: 0.7, // Slightly fade disabled options
                                  // Explicitly remove hover effects when disabled
                                  transform: 'none',
                                  boxShadow: 'none',
                                  cursor: 'default',
                              },
                            }}
                          >
                            {renderLatexOrText(choice, 'normal', true)}
                          </Button>
                        );
                      })}
                    </Stack> // End Stack for choices
                  )}
                </Box> {/* End Right Column */}
              </Box> {/* End Flex Container for Question/Answers */}

              {/* Navigation buttons */}
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Button variant='contained'
                  disabled={seriesState.currentQuestionIndex === 0}
                  onClick={handlePreviousQuestion}
                  startIcon={<ArrowBackIcon />}
                > 
                Previous Question
                </Button>

                <Box sx={{ display: 'flex', gap: {xs: 1, sm: 2}, marginLeft: {xs: 1, sm: 2 } }}>
                  <Button variant="outlined" 
                    onClick={// If it's a milestone question and free response, handle like a submit
                      (choices.length === 0 && (seriesState.currentQuestionIndex + 1) % 5 === 0) ? 
                        handleSubmit :  handleSkip
                    } endIcon={<SkipNextIcon />}
                    // Disable skip if it's the last question of 5 or if correct answer was just submitted
                    disabled={// Only disable if:
                      // 1. Question is multiple choice (has choices) AND
                      // 2. Either:
                      //    - Answer is correct and submitted, OR
                      //    - It's a milestone question (5, 10, 15, etc.)
                      (choices.length > 0 && (
                        (isAnswerSubmitted && selectedAnswer === correctAnswer) || 
                        (seriesState.currentQuestionIndex + 1) % 5 === 0
                      ))}
                  > {(choices.length === 0 && (seriesState.currentQuestionIndex + 1) % 5 === 0) ? 
                    'Show Answer' : 
                    'Skip Question'
                    }
                  </Button>

                  <Button variant="contained" //handles both submit and next question once user gets it right
                    onClick={isAnswerSubmitted && feedback === 'Correct!' ? 
                      (seriesState.currentQuestionIndex + 1 >= seriesState.totalQuestionsInSeries ? 
                        handleExtendSeries : handleNextQuestion) : 
                      handleSubmit}
                      sx={{px: {xs: 1, sm: 2}}}
                  >
                    {isAnswerSubmitted && feedback === 'Correct!' ?
                      (seriesState.currentQuestionIndex + 1 >= seriesState.totalQuestionsInSeries ?
                        'Practice 5 More!' :
                        'Next Question') :
                      'Submit Answer'}
                  </Button>
                </Box>
              </Box>

              {/*4/1/2025: Chat component moved above explanation because Luke Coleman (student) didn't notice before and only read explanations.*/}
              {interactionStage !== 'notStarted' && 
                <Box sx={{marginTop: 2}}>
                  <ChatComponent />
                </Box>
              }
          
              {choices.length === 0 ? 
                (isAnswerSubmitted && freeResponseInput.trim() === '' ? (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1,border: 1, borderColor: 'primary.main'}}>
                    <Typography variant="h6" sx={{ mb: 1, color: 'primary.main', fontWeight: 'bold'}}>{feedback}</Typography>
                    {/*<Typography variant="body1"> {feedback} </Typography>*/}
                  </Box>
                ) : (isAnswerSubmitted && 
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1,border: 1, borderColor: 'primary.main'}}>
                    {/*In place of {feedback} below, I used to have "Explanation:" */}
                    <Typography variant="h6" sx={{ mb: 1, color: 'primary.main', fontWeight: 'bold'}}>{feedback}</Typography>
                    <Typography variant="body1"> {renderLatexOrText(explanationText, 'normal', true)} </Typography>
                  </Box>
                )
              ) 
              : (isAnswerSubmitted && selectedAnswer === correctAnswer && feedback && (
                  <Box sx={{ my: 2, p: 2, bgcolor: 'background.paper',
                      borderRadius: 1,border: 1, borderColor: 'primary.main',
                    }}
                    >
                    <Typography variant="h6" sx={{ mb: 1, color: 'primary.main', fontWeight: 'bold'}}>
                      Explanation:
                    </Typography>
                    <Typography variant="body1"> {renderLatexOrText(explanationText, 'normal', true)} </Typography>
                  </Box>
                ))
              }
              
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
      <CelebrationRenderer celebrationAnimation={celebrationAnimation} />
    </Box>
  );
};

export default SkillButtonsAndQuestionGenerator;
